import { OrthographicCamera } from 'three/webgpu';
import { Config } from '@src/core/Config';

const MARGIN = 1.2;

/** Top-down orthographic camera that letterboxes the whole room into the viewport. */
export class ArenaCamera {
  readonly camera = new OrthographicCamera(-1, 1, 1, -1, 0.1, 100);
  private halfW = Config.room.width / 2 + MARGIN;
  private halfH = Config.room.height / 2 + MARGIN;
  private viewportW = 1;
  private viewportH = 1;
  shakeX = 0;
  shakeY = 0;
  /** World-space look target; rooms sit at the origin, the next room offsets during a walk. */
  centerX = 0;
  centerY = 0;

  constructor() {
    this.camera.position.set(0, 0, 20);
    this.camera.lookAt(0, 0, 0);
  }

  resize(width: number, height: number): void {
    this.viewportW = Math.max(1, width);
    this.viewportH = Math.max(1, height);
    const roomAspect = (Config.room.width + MARGIN * 2) / (Config.room.height + MARGIN * 2);
    const viewAspect = this.viewportW / this.viewportH;
    if (viewAspect >= roomAspect) {
      this.halfH = Config.room.height / 2 + MARGIN;
      this.halfW = this.halfH * viewAspect;
    } else {
      this.halfW = Config.room.width / 2 + MARGIN;
      this.halfH = this.halfW / viewAspect;
    }
    this.apply();
  }

  private apply(): void {
    const c = this.camera;
    const ox = this.centerX + this.shakeX;
    const oy = this.centerY + this.shakeY;
    c.left = -this.halfW + ox;
    c.right = this.halfW + ox;
    c.top = this.halfH + oy;
    c.bottom = -this.halfH + oy;
    c.updateProjectionMatrix();
  }

  setCenter(x: number, y: number): void {
    this.centerX = x;
    this.centerY = y;
    this.apply();
  }

  setShake(x: number, y: number): void {
    this.shakeX = x;
    this.shakeY = y;
    this.apply();
  }

  /** World coordinates -> CSS pixels (for DOM popups). */
  worldToScreen(wx: number, wy: number, out: Float32Array): void {
    const nx = (wx - this.centerX - this.shakeX) / this.halfW;
    const ny = (wy - this.centerY - this.shakeY) / this.halfH;
    out[0] = ((nx + 1) / 2) * this.viewportW;
    out[1] = ((1 - ny) / 2) * this.viewportH;
  }

  /** Screen pixel -> world coordinates on the z=0 plane. */
  screenToWorld(sx: number, sy: number, out: Float32Array): void {
    const nx = (sx / this.viewportW) * 2 - 1;
    const ny = 1 - (sy / this.viewportH) * 2;
    out[0] = nx * this.halfW + this.centerX + this.shakeX;
    out[1] = ny * this.halfH + this.centerY + this.shakeY;
  }
}
